import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  MapPin,
  Mail,
  Phone,
  User,
  Building,
  Trash2,
  X,
  Check,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useNotifications } from '@/hooks/useNotifications';
import {
  usePartnershipRequests,
  useUpdatePartnershipRequestStatus,
  useDeletePartnershipRequest,
} from '@/hooks/usePartnershipRequests';
import { formatDate } from '@/utils';
import { PartnershipRequest } from '@/api/partnershipRequests';

const ALL_VALUE = 'all';

const REJECTION_REASONS: Record<string, string> = {
  'Business Not Registered / Verified':
    'Merchant cannot provide valid business registration or cannot pass verification.',
  'Region-Specific Restriction':
    'The product/service is legal generally but not permitted in the target country/state/region.',
  'Product Cannot Be Verified':
    'Merchant cannot provide sufficient information/proof to verify the product or service.',
  'Prohibited Category': 'The product belongs to a category your platform does not support.',
  Other: '',
};

export function PartnershipRequestsPage() {
  const { success, error: showError } = useNotifications();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(7);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_VALUE);
  const [rejectionType, setRejectionType] = useState<string>('');

  // Modal / Confirm States
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    request?: PartnershipRequest;
    action?: 'Approved' | 'Rejected';
  }>({
    open: false,
  });
  const [rejectionReason, setRejectionReason] = useState('');

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    request?: PartnershipRequest;
  }>({
    open: false,
  });

  const { data, isLoading, isFetching } = usePartnershipRequests({
    skip: page * pageSize,
    take: pageSize,
    status: statusFilter !== ALL_VALUE ? statusFilter : undefined,
  });

  const { mutate: updateStatus, isPending: isUpdating } = useUpdatePartnershipRequestStatus();
  const { mutate: deleteRequest, isPending: isDeleting } = useDeletePartnershipRequest();

  // Normalize data fetching - handle both paginated objects and arrays defensively
  const requests = Array.isArray(data) ? data : (data?.data ?? []);
  const total = Array.isArray(data) ? data.length : (data?.count ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleActionClick = (request: PartnershipRequest, action: 'Approved' | 'Rejected') => {
    setRejectionReason('');
    setRejectionType('');
    setConfirmDialog({
      open: true,
      request,
      action,
    });
  };

  const handleRejectionTypeChange = (type: string) => {
    setRejectionType(type);
    if (type && type in REJECTION_REASONS) {
      setRejectionReason(REJECTION_REASONS[type] || '');
    } else {
      setRejectionReason('');
    }
  };

  const handleDeleteClick = (request: PartnershipRequest) => {
    setDeleteDialog({
      open: true,
      request,
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteDialog.request) return;
    const { id, companyName, businessName, name } = deleteDialog.request;
    const requestName = companyName || businessName || name || 'Vendor';

    deleteRequest(id, {
      onSuccess: () => {
        success(`Partnership request for "${requestName}" has been deleted.`);
        setDeleteDialog({ open: false });
      },
      onError: (err) => {
        showError(err.response?.data?.message || 'Failed to delete partnership request.');
      },
    });
  };

  const handleConfirmAction = () => {
    if (!confirmDialog.request || !confirmDialog.action) return;
    if (confirmDialog.action === 'Rejected' && !rejectionReason.trim()) return;

    const { id, companyName, businessName, name } = confirmDialog.request;
    const requestName = companyName || businessName || name || 'Vendor';
    const nextStatus = confirmDialog.action;

    updateStatus(
      {
        requestId: id,
        status: nextStatus,
        rejectionReason: nextStatus === 'Rejected' ? rejectionReason.trim() : undefined,
      },
      {
        onSuccess: () => {
          success(
            `Partnership request for "${requestName}" has been successfully ${nextStatus.toLowerCase()}.`
          );
          setConfirmDialog({ open: false });
        },
        onError: (err) => {
          const errMsg =
            err.response?.data?.message || `Failed to update status to ${nextStatus.toLowerCase()}`;
          showError(errMsg);
        },
      }
    );
  };

  const getStatusBadge = (request: PartnershipRequest) => {
    const { status, rejectionReason } = request;
    switch (status) {
      case 'Approved':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20">
            <CheckCircle className="h-3.5 w-3.5" />
            Approved
          </span>
        );
      case 'Rejected':
        return (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20 cursor-help"
            title={`Rejection Reason: ${rejectionReason || 'No reason provided'}`}
          >
            <XCircle className="h-3.5 w-3.5" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20">
            <Clock className="h-3.5 w-3.5 animate-pulse" />
            Pending
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Partnership Requests</h2>
          <p className="text-muted-foreground">Review and manage merchant partnership signups.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="w-full sm:max-w-[200px]">
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setPage(0);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All requests</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Requests Table */}
      <div className="rounded-lg border bg-card overflow-auto max-h-[600px]">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead className="sticky top-0 bg-background z-10">
              <tr className="border-b bg-muted/50 text-left text-muted-foreground whitespace-nowrap">
                <th className="p-4 font-medium">Company Name</th>
                <th className="p-4 font-medium">Contact Person</th>
                <th className="p-4 font-medium">Contact Mobile</th>
                <th className="p-4 font-medium">Business Email</th>
                <th className="p-4 font-medium">Category</th>
                {/* <th className="p-4 font-medium">Custom Category</th> */}
                <th className="p-4 font-medium">City</th>
                <th className="p-4 font-medium">State</th>
                <th className="p-4 font-medium">Website</th>
                <th className="p-4 font-medium">Rewards Proposal</th>
                <th className="p-4 font-medium">Date Received</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={13} className="p-8 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      Loading partnership requests...
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && requests.length === 0 && (
                <tr>
                  <td colSpan={13} className="p-8 text-center text-muted-foreground">
                    No partnership requests found.
                  </td>
                </tr>
              )}
              {!isLoading &&
                requests.map((request) => {
                  const reqName =
                    request.companyName || request.businessName || request.name || 'N/A';
                  const proposal = request.rewardsProposal || request.description || '';
                  const truncatedProposal =
                    proposal.length > 50 ? `${proposal.slice(0, 50)}...` : proposal;
                  const mobileNum = request.contactMobile
                    ? request.contactMobile.replace(/^\+91/, '')
                    : 'N/A';

                  return (
                    <tr
                      key={request.id}
                      className="border-b last:border-0 hover:bg-muted/10 transition-colors"
                    >
                      <td className="p-4">
                        <span className="font-semibold text-foreground flex items-center gap-1.5">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          {reqName}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="font-medium text-foreground">
                          {request.contactPerson || 'N/A'}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground">{mobileNum}</td>
                      <td className="p-4 text-muted-foreground">
                        {request.businessEmail || 'N/A'}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {typeof request.industryCategory === 'object'
                          ? request.industryCategory?.name
                          : request.industryCategory || 'N/A'}
                      </td>
                      {/* <td className="p-4 text-muted-foreground">
                        {request.customCategories && request.customCategories.length > 0
                          ? request.customCategories
                              .map((c: any) => (typeof c === 'object' ? c.name : c))
                              .join(', ')
                          : '-'}
                      </td> */}
                      <td className="p-4 text-muted-foreground">{request.city || '-'}</td>
                      <td className="p-4 text-muted-foreground">{request.state || '-'}</td>
                      <td className="p-4">
                        {request.websiteUrl ? (
                          <a
                            href={
                              request.websiteUrl.startsWith('http')
                                ? request.websiteUrl
                                : `https://${request.websiteUrl}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline animate-fade-in"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Website
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>
                      <td className="p-4 max-w-xs truncate text-muted-foreground" title={proposal}>
                        {truncatedProposal || '-'}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {request.createdAt ? formatDate(request.createdAt) : '-'}
                      </td>
                      <td className="p-4">{getStatusBadge(request)}</td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {request.status === 'Pending' ? (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-900/40"
                                onClick={() => handleActionClick(request, 'Rejected')}
                                title="Reject Request"
                              >
                                <X className="h-5 w-5 stroke-[2.5]" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 rounded-lg bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-950/40 dark:text-green-400 dark:hover:bg-green-900/40"
                                onClick={() => handleActionClick(request, 'Approved')}
                                title="Approve Request"
                              >
                                <Check className="h-5 w-5 stroke-[2.5]" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 bg-red-50 text-destructive hover:bg-red-100 hover:text-destructive dark:bg-red-950/20 dark:text-red-400 dark:hover:bg-red-950/40"
                              onClick={() => handleDeleteClick(request)}
                              title="Delete Request"
                              disabled={isDeleting}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col items-center justify-between gap-3 border-t p-4 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[7, 10, 25, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              Page {total === 0 ? 0 : page + 1} of {totalPages} ({total} total)
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page === 0 || isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page + 1 >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(isOpen) => !isOpen && setConfirmDialog({ open: false })}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.action === 'Approved' ? 'Approve Partnership' : 'Reject Partnership'}
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to {confirmDialog.action?.toLowerCase()} the partnership request
              for{' '}
              <strong className="text-foreground">
                {confirmDialog.request?.companyName ||
                  confirmDialog.request?.businessName ||
                  confirmDialog.request?.name ||
                  'this vendor'}
              </strong>
              ?
            </DialogDescription>
            {confirmDialog.action === 'Rejected' && (
              <div className="mt-4 space-y-4 text-left">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">
                    Rejection Type <span className="text-destructive">*</span>
                  </label>
                  <Select value={rejectionType} onValueChange={handleRejectionTypeChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select rejection type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Business Not Registered / Verified">
                        Business Not Registered / Verified
                      </SelectItem>
                      <SelectItem value="Region-Specific Restriction">
                        Region-Specific Restriction
                      </SelectItem>
                      <SelectItem value="Product Cannot Be Verified">
                        Product Cannot Be Verified
                      </SelectItem>
                      <SelectItem value="Prohibited Category">Prohibited Category</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">
                    Rejection Reason <span className="text-destructive">*</span>
                  </label>
                  <Textarea
                    placeholder="Enter rejection reason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="min-h-[100px]"
                    readOnly={rejectionType !== 'Other' && rejectionType !== ''}
                  />
                </div>
              </div>
            )}
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            {confirmDialog.action === 'Approved' ? (
              <>
                <Button
                  variant="outline"
                  disabled={isUpdating}
                  onClick={() => setConfirmDialog({ open: false })}
                >
                  Cancel
                </Button>
                <Button
                  disabled={isUpdating}
                  onClick={handleConfirmAction}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white border-none"
                >
                  {isUpdating ? 'Processing...' : 'Approve'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => setConfirmDialog({ open: false })}
                  className="bg-[#A30000] hover:bg-[#820000] text-white border-none"
                >
                  Close
                </Button>
                <Button
                  type="button"
                  disabled={isUpdating || !rejectionType || !rejectionReason.trim()}
                  onClick={handleConfirmAction}
                  className="bg-[#00B4D8] hover:bg-[#0096C7] text-white border-none"
                >
                  {isUpdating ? 'Processing...' : 'Submit'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(isOpen) => !isOpen && setDeleteDialog({ open: false })}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Partnership Request</DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete the partnership request for{' '}
              <strong className="text-foreground">
                {deleteDialog.request?.companyName ||
                  deleteDialog.request?.businessName ||
                  deleteDialog.request?.name ||
                  'this vendor'}
              </strong>
              ? This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button
              type="button"
              disabled={isDeleting}
              onClick={() => setDeleteDialog({ open: false })}
              className="bg-[#A30000] hover:bg-[#820000] text-white border-none"
            >
              Close
            </Button>
            <Button
              type="button"
              disabled={isDeleting}
              onClick={handleConfirmDelete}
              className="bg-[#00B4D8] hover:bg-[#0096C7] text-white border-none"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
